<template>
  <div class="lot-items">
    <div v-for="(row, index) in items" :key="row._id" class="lot-items-card">
      <div class="lot-items-card-header">
        <div v-if="false" class="lot-items-card-title">{{ $t("lot_items.lot") }} {{ index + 1 }}</div>
        <button type="button" class="lot-items-remove" @click="removeRow(index)">
          {{ $t("common.remove") }}
        </button>
      </div>
      <div class="lot-items-field">
        <div class="reception_input_holder">
          <div class="input_label">{{ $t("lot_items.lot") }}</div>
          <div class="input_value">
            <DxTextBox v-model="row.lot" class="global_dx_textbox" :placeholder="$t('lot_items.lot_placeholder')" @input="emitChange" />
          </div>
        </div>
      </div>
      <div class="lot-items-field">
        <div class="reception_input_holder" @click="toggleCalendar(row)">
          <div class="input_label">{{ $t("common.expiration_date") }}</div>
          <div class="input_value">
            <div class="global_dx_textbox lot-items-date-text">
              <span v-if="row.expiration_date" class="lot-items-date">{{ row.expiration_date }}</span>
              <span v-else class="lot-items-date-placeholder">{{ $t("lot_items.expiration_placeholder") }}</span>
            </div>
          </div>
        </div>
        <div v-if="openCalendarId === row._id" class="lot-items-calendar">
          <FunctionalCalendar
            :date-format="getDefaultDateFormat.toLowerCase()"
            :is-date-range="false"
            v-model="row.calendarData"
            :change-month-function="true"
            :change-year-function="true"
            :is-date-picker="true"
            @choseDay="calendarDayChosen"
            @input="onDateSelected"
          ></FunctionalCalendar>
        </div>
      </div>
      <div class="lot-items-field">
        <div class="reception_input_holder">
          <div class="input_label">{{ $t("common.quantity") }}</div>
          <div class="input_value">
            <DxNumberBox
              v-model="row.quantity"
              :min="0"
              :format="quantityFormat"
              value-change-event="keyup"
              class="global_dx_number_input"
              :show-clear-button="false"
              @value-changed="emitChange"
            />
          </div>
        </div>
      </div>
    </div>

    <button type="button" class="lot-items-add" @click="addRow">
      {{ $t("lot_items.add") }}
    </button>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import DxTextBox from "devextreme-vue/text-box";
import DxNumberBox from "devextreme-vue/number-box";
import { FunctionalCalendar } from "vue-functional-calendar";

export default {
  name: "LotItemsEditor",
  components: { DxTextBox, DxNumberBox, FunctionalCalendar },
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    measureUnitSymbol: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      items: this.cloneItems(this.value),
      openCalendarId: null,
    };
  },
  computed: {
    ...mapGetters({
      getDefaultDateFormat: "settings/getDefaultDateFormat",
    }),
    quantityFormat() {
      return `#,##0.#### ${this.measureUnitSymbol || ""}`.trim();
    },
  },
  watch: {
    value: {
      deep: true,
      handler(newVal) {
        this.items = this.cloneItems(newVal, this.items);
      },
    },
  },
  methods: {
    cloneItems(items, existingItems = []) {
      if (!Array.isArray(items)) {
        return [];
      }
      return items.map((item, index) => ({
        _id: item._id || existingItems[index]?._id || this.newId(),
        lot: item.lot || "",
        expiration_date: item.expiration_date || null,
        quantity: this.toNumber(item.quantity),
      }));
    },
    newId() {
      return Math.random().toString(36).slice(2);
    },
    toNumber(value) {
      const num = typeof value === "number" ? value : parseFloat(value);
      return Number.isFinite(num) ? num : 0;
    },
    emitChange() {
      const payload = this.items.map(({ _id, ...item }) => ({
        lot: item.lot,
        expiration_date: item.expiration_date,
        quantity: this.toNumber(item.quantity),
      }));
      this.$emit("input", payload);
    },
    addRow() {
      this.items.push({
        _id: this.newId(),
        lot: "",
        expiration_date: null,
        quantity: 0,
        calendarData: {},
      });
      this.emitChange();
    },
    toggleCalendar(row) {
      if (!row || !row._id) {
        return;
      }
      this.openCalendarId = this.openCalendarId === row._id ? null : row._id;
    },
    calendarDayChosen() {
      this.openCalendarId = null;
    },
    onDateSelected(date) {
      const row = this.items.find((r) => r._id === this.openCalendarId);
      if (!row) {
        return;
      }
      row.expiration_date = date?.selectedDate || null;
      this.emitChange();
      this.openCalendarId = null;
    },
    removeRow(index) {
      this.items.splice(index, 1);
      this.emitChange();
    },
  },
};
</script>

<style scoped>
.lot-items {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.lot-items-add {
  background: #0a7a2f;
  border: none;
  color: #ffffff;
  padding: 10px 12px;
  border-radius: 56px;
  font-weight: 600;
  width: 80%;
  display: block;
  margin: 0 auto;
}

.lot-items-card {
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding: 8px;
  border: 1px solid #e7e7e7;
  border-radius: 10px;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.lot-items-card-header {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  gap: 8px;
}

.lot-items-card-title {
  font-weight: 600;
  font-size: 13px;
  color: #1f1f1f;
}

.lot-items-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.lot-items-remove {
  margin-left: auto;
  background: #fff1f1;
  border: 1px solid #f1b9b9;
  color: #b62323;
  font-size: 12px;
  font-weight: 600;
  padding: 6px 8px;
  border-radius: 8px;
}

.lot-items-calendar {
  margin-top: 8px;
}

.lot-items-date-text {
  padding: 8px;
}

.lot-items-date-placeholder {
  font-family: "Mulish";
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  letter-spacing: 0.02em;

  /* Gray 80 */
  color: #919090;
}

.lot-items-date {
  font-weight: 700;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 0.02em;
  color: #565656;
}
</style>
